import { z } from "zod";

import type {
  RoleFormType,
  RoleParams,
} from "@js/apps/common/components/filters/forms/role-form";
import {
  defaultRole,
  defaultRoleParams,
} from "@js/apps/common/components/filters/forms/role-form";
import { defaultExperience } from "@js/apps/common/components/filters/forms/years-of-experience-form";
import {
  createFiltersForm,
  createFiltersSearchParamsHook,
  FiltersManager,
} from "@js/apps/filters/";
import type { UTCOffset } from "@js/apps/filters/types";
import { normalizeRolesAndSkillsFormat } from "@js/apps/filters/utils";
import {
  CUSTOM_RATE_VALUE,
  isRateOptionSelected,
} from "@js/apps/jobs/apps/listing/const";
import {
  type LocationValue,
  LocationValueType,
} from "@js/components/autocomplete-new/google-places/types";
import { mapAddressComponentsToAddressForJobSearch } from "@js/components/autocomplete-new/google-places/utils";
import { zodStringBoolean } from "@js/rhf/schemas/boolean";
import type { SearchEventEntityTypeKey } from "@js/types/tracking";

import {
  defaultSkills,
  defaultSkillsParams,
  type SkillsFormType,
  type SkillsParams,
} from "./forms/skills-form";

export type OrderingType = "-created" | "-score" | "newest" | "-search_score";
export type Ordering = { ordering?: OrderingType };
export type Search = { search?: string };
type ContractType = { contract_type?: EnumType<typeof ENUMS.JobContractType> };
type PaymentType = { payment_type?: EnumType<typeof ENUMS.JobContractType> };

export type UIUsedParams = {
  job: number;
  approved: undefined | true;
  invite_to_job: true;
  page?: number;
  skill_search_operator: "and";
};

type Rate = {
  hourly_budget_minimum_usd?: number | typeof CUSTOM_RATE_VALUE;
  custom_rate?: number;
};

type RateParams = { hourly_budget_minimum_usd?: number };

export type Location = {
  work_from_anywhere?: boolean;
  work_from_location?: boolean;
  custom_location?: string;
  country_code?: string;
  state?: string;
  city?: string;
  location?: string;
  place_id?: string; // in talent filters
};

export type LocationFormValues = {
  work_from_anywhere?: boolean;
  work_from_location?: boolean;
  custom_location?: string;
  country_code?: string;
  state?: string;
  city?: string;
  location?: LocationValue;
};

export type TalentLocation = {
  custom_location?: [];
  place_id?: [];
  include_verified_locations_only?: boolean;
};

export type YearsOfExperienceRange = "1-3" | "3-5" | "5-10" | "10+";

export type YearsOfExperience = {
  experience: YearsOfExperienceRange[];
};

type JobType = { job_type?: string[] };

export type ApprovalStatus = { approved?: boolean; talent_status?: string[] };

type AvailabilityForm = { availability?: [number, number] };
type AvailabilityParams = {
  availability_from: number | undefined;
  availability_to: number | undefined;
};

type TalentAvailabilityOption = "full_time" | "part_time" | "direct_hire";
type TalentAvailability = {
  applied_recently?: boolean;
  looking_for_work?: boolean;
  looking_for_work_options?: TalentAvailabilityOption[];
};
type TalentAvailabilityParams = {
  applied_recently?: boolean;
  looking_for_work?: string;
  looking_for_work_options?: string;
};

export type WorkingTimezoneJobs = {
  match_my_working_hours?: boolean;
};

export const HOURS_OVERLAP = {
  full_work_day: "full_work_day",
  few_hours: "few_hours",
} as const;

export type HoursOverlap = keyof typeof HOURS_OVERLAP;

export const HOURS_OVERLAP_MAP: Record<
  HoursOverlap,
  { value: HoursOverlap; label: string; onFilterButtonLabel: string }
> = {
  full_work_day: {
    value: "full_work_day",
    label: "The full workday",
    onFilterButtonLabel: "Full day overlap",
  },
  few_hours: {
    label: "A few hours",
    value: "few_hours",
    onFilterButtonLabel: "Partial overlap",
  },
};

export type WorkingTimezoneTalents =
  | {
      timezone: UTCOffset;
      hours_overlap: HoursOverlap;
    }
  | {
      timezone?: undefined;
      hours_overlap?: undefined;
    };
export type JobFilters = ContractType &
  PaymentType &
  Rate &
  Location &
  Ordering &
  Search &
  ApprovalStatus &
  JobType &
  WorkingTimezoneJobs &
  Partial<RoleFormType>;

export type PublicJobFilters = ContractType & Rate & Location & JobType;

export type TalentFilters = Rate &
  TalentLocation &
  Ordering &
  Search &
  YearsOfExperience &
  TalentAvailability &
  WorkingTimezoneTalents &
  RoleFormType &
  ApprovalStatus;

export type JobFiltersFormValuesType = JobFilters &
  SkillsFormType &
  AvailabilityForm &
  RoleFormType;

export type JobFiltersFetchParams = JobFilters &
  SkillsParams &
  AvailabilityParams &
  RoleParams;

export type PublicJobFiltersFormValuesType = PublicJobFilters &
  SkillsFormType &
  AvailabilityForm &
  RoleFormType;

export type PublicJobFiltersFetchParams = PublicJobFilters &
  SkillsParams &
  AvailabilityParams &
  RoleParams &
  Pick<UIUsedParams, "page">;

export type TalentFiltersFormValuesType = TalentFilters &
  SkillsFormType &
  RoleFormType;

export type TalentFiltersFetchParams = TalentFilters &
  SkillsParams &
  RoleParams &
  UIUsedParams;

export type SearchDataType = SearchEventEntityTypeKey;

export const defaultSearch: Search = { search: undefined };

export const SearchForm = createFiltersForm<Search>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultSearch,
  }),
);

////////////////////////////////////////////////////////////////////////////

export const defaultPage = { page: undefined };

////////////////////////////////////////////////////////////////////////////

// default filters must contain the fields, even if values should be undefined
const defaultOrdering: Ordering = { ordering: undefined };

export const SortForm = createFiltersForm<Ordering>(
  new FiltersManager({
    submitOnChange: true,
    useAllURLParams: true,
    defaultFormValues: defaultOrdering,
  }),
);

////////////////////////////////////////////////////////////////////////////

const defaultRate: Rate = {
  hourly_budget_minimum_usd: undefined,
  custom_rate: undefined,
};

export const RateForm = createFiltersForm<Rate, RateParams>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultRate,
    mapValuesToParams: (values) => {
      const newValues = {
        ...values,
      };

      if (newValues.hourly_budget_minimum_usd === CUSTOM_RATE_VALUE) {
        newValues.hourly_budget_minimum_usd = newValues.custom_rate;
      }

      delete newValues.custom_rate;

      return newValues as RateParams;
    },
    mapParamsToValues: (values) => {
      const newValues = {
        ...values,
      } as Rate;

      const minBudget = values.hourly_budget_minimum_usd;

      if (minBudget && !isRateOptionSelected(minBudget)) {
        newValues.custom_rate = minBudget;
        newValues.hourly_budget_minimum_usd = CUSTOM_RATE_VALUE;
      }

      return newValues;
    },
  }),
);

////////////////////////////////////////////////////////////////////////////

const defaultLocation: Location = {
  work_from_anywhere: undefined,
  work_from_location: undefined,
  custom_location: undefined,
  country_code: undefined,
};

const mapLocationFormValuesToParams = (values: LocationFormValues) => {
  const newValues = {
    ...values,
  };

  if (!values.work_from_anywhere) {
    newValues.work_from_anywhere = undefined;
  }

  if (!values.work_from_location) {
    newValues.work_from_location = undefined;
    newValues.location = undefined;
    newValues.country_code = undefined;
    newValues.city = undefined;
    newValues.state = undefined;
    newValues.custom_location = undefined;

    return newValues as Location;
  }

  delete newValues.location;

  if (values.location?.location_type === LocationValueType.custom) {
    return {
      ...newValues,
      custom_location: values.location.custom_location,
      location: values.location.location,
      country_code: undefined,
      city: undefined,
      state: undefined,
    } as Location;
  }

  const addressComponents = values.location?.place_details?.address_components;
  if (addressComponents) {
    const { city, country, state } =
      mapAddressComponentsToAddressForJobSearch(addressComponents);

    return {
      ...newValues,
      location: values.location?.location,
      city,
      state,
      country_code: country,
      custom_location: undefined,
    } as Location;
  }

  // when location is initialized by params (previous values), not selected from autocomplete
  return {
    ...newValues,
    location: values.location?.location,
  };
};

const mapLocationParamsToFormValues = (values: Location) => {
  const newValues = {
    ...values,
  };

  if (!newValues.work_from_anywhere) {
    newValues.work_from_anywhere = undefined;
  }

  if (!newValues.work_from_location) {
    newValues.work_from_location = undefined;
    newValues.location = undefined;
    newValues.country_code = undefined;
    newValues.city = undefined;
    newValues.state = undefined;
    newValues.custom_location = undefined;
  }

  delete newValues.location;

  // we transform to required format directly within a form
  return {
    ...newValues,
    location: undefined,
  } as LocationFormValues;
};

export const LocationForm = createFiltersForm<LocationFormValues, Location>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultLocation as LocationFormValues,
    mapValuesToParams: mapLocationFormValuesToParams,
    mapParamsToValues: mapLocationParamsToFormValues,
  }),
);

const defaultTalentLocation: TalentLocationFormValues = {
  location: [],
  include_verified_locations_only: undefined,
};

export type TalentLocationFormValues = {
  location?: LocationValue[];
  include_verified_locations_only?: boolean;
};

export const TalentLocationForm = createFiltersForm<
  TalentLocationFormValues,
  TalentLocation
>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultTalentLocation,
    mapValuesToParams: (values: TalentLocationFormValues) => {
      const otherValues = { ...values };
      const place_id: string[] = [];
      const custom_location: string[] = [];

      values.location?.forEach((location) => {
        if (location.location_type === LocationValueType.custom) {
          custom_location.push(location.custom_location);
        } else if (location.location_type === LocationValueType.google) {
          place_id.push(location.place_id);
        }
      });

      delete otherValues.location;

      return {
        ...otherValues,
        place_id,
        custom_location,
        include_verified_locations_only: values.include_verified_locations_only,
      } as TalentLocation;
    },
    mapParamsToValues: (values: TalentLocation) => {
      /** to initialize properly we need to fetch entities based on ids from query, so we want to return original query params */
      return values;
    },
  }),
);

////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////

const defaultJobType: JobType = {
  job_type: [],
};

export const JobTypeForm = createFiltersForm<JobType>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultJobType,
  }),
);

////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////

const defaultCommitment: AvailabilityForm = {
  availability: [5, 40],
};

const defaultCommitmentParams: AvailabilityParams = {
  availability_from: 5,
  availability_to: 40,
};

const defaultCommitmentFilters: AvailabilityForm = {
  availability: undefined,
};

const defaultCommitmentParamsFilters: AvailabilityParams = {
  availability_from: undefined,
  availability_to: undefined,
};

export const CommitmentForm = createFiltersForm<
  AvailabilityForm,
  AvailabilityParams
>(
  new FiltersManager({
    useAllURLParams: true,
    allowToSubmitPristineForm: true,
    defaultFormValues: defaultCommitment,
    defaultParamsValues: defaultCommitmentParams,
    mapValuesToParams: (values) => {
      const newFilters = { ...values } as AvailabilityForm & AvailabilityParams;

      if (newFilters.availability) {
        newFilters.availability_from = newFilters.availability[0];
        newFilters.availability_to = newFilters.availability[1];
        delete newFilters.availability;
      }

      return newFilters as AvailabilityParams;
    },
    mapParamsToValues: (values) => {
      const newFilters = { ...values } as AvailabilityForm & AvailabilityParams;

      if (newFilters.availability_from || newFilters.availability_to) {
        newFilters.availability = [
          newFilters?.availability_from || 0,
          newFilters?.availability_to || 0,
        ];
      }

      delete newFilters.availability_from;
      delete newFilters.availability_to;

      return newFilters as AvailabilityForm;
    },
  }),
);

////////////////////////////////////////////////////////////////////////////

const defaultTalentAvailabilityFilter: TalentAvailability = {
  applied_recently: undefined,
  looking_for_work: undefined,
  looking_for_work_options: [],
};

export const talentAvailabilityOptionSet = new Set<string>(
  Object.values(ENUMS.FreelancerAvailabilityForWork),
);

const isTalentAvailabilityOption = (
  param: string,
): param is TalentAvailabilityOption => {
  return !!param && talentAvailabilityOptionSet.has(param);
};

export const TalentAvailabilityForm = createFiltersForm<
  TalentAvailability,
  TalentAvailabilityParams
>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultTalentAvailabilityFilter,
    mapValuesToParams: (values) => {
      const newValues = { ...values } as TalentAvailabilityParams;

      if (values.looking_for_work_options?.length) {
        newValues.looking_for_work = values.looking_for_work_options.join(",");
      }

      if (!newValues.looking_for_work) {
        delete newValues.looking_for_work;
      }

      delete newValues.looking_for_work_options;

      return newValues;
    },
    mapParamsToValues: (values) => {
      const newValues = { ...values } as TalentAvailability;

      if (values.looking_for_work) {
        newValues.looking_for_work_options = values.looking_for_work
          .split(",")
          .filter(isTalentAvailabilityOption);
      }

      return newValues;
    },
  }),
);
////////////////////////////////////////////////////////////////////////////

export const defaultWorkingTimezoneJobFilter = {
  match_my_working_hours: undefined,
} satisfies WorkingTimezoneJobs;

const defaultWorkingTimezoneJobFormValues: WorkingTimezoneJobs =
  defaultWorkingTimezoneJobFilter;

export const WorkingTimezoneJobsForm = createFiltersForm<WorkingTimezoneJobs>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultWorkingTimezoneJobFormValues,
  }),
);
////////////////////////////////////////////////////////////////////////////

export const defaultWorkingTimezoneTalentFilter: WorkingTimezoneTalents = {
  timezone: undefined,
  hours_overlap: undefined,
};

export const workingTimezoneTalentFilterShape = z.object({
  timezone: z.custom<UTCOffset>(
    (val: unknown) =>
      !!val &&
      typeof val === "string" &&
      /^[+-][0-9][0-9]:[0-9][0-9]/.test(val),
  ),
  hours_overlap: z.nativeEnum(HOURS_OVERLAP),
});

const mapWorkingTimezoneTalentFiltersParams = <
  T extends WorkingTimezoneTalents,
>(
  newValues: T,
): T => {
  const valuesCopy = { ...newValues };

  const parseResult = workingTimezoneTalentFilterShape.safeParse(valuesCopy);
  if (!parseResult.success) {
    delete valuesCopy.timezone;
    delete valuesCopy.hours_overlap;
  }

  return valuesCopy;
};

export const WorkingTimezoneTalentsForm = createFiltersForm<
  WorkingTimezoneTalents,
  WorkingTimezoneTalents
>(
  new FiltersManager({
    useAllURLParams: true,
    defaultFormValues: defaultWorkingTimezoneTalentFilter,
    mapParamsToValues: (values) => {
      return mapWorkingTimezoneTalentFiltersParams(values);
    },
  }),
);

////////////////////////////////////////////////////////////////////////////

const defaultCommonFilters = {
  ...defaultSearch,
  ...defaultRate,
  ...defaultLocation,
  ...defaultJobType,
};

export const useJobFilters = createFiltersSearchParamsHook<
  JobFiltersFormValuesType,
  JobFiltersFetchParams
>(
  new FiltersManager({
    paramsToIgnore: ["show_saved_searches_drawer"], // just for ui purpose
    defaultFormValues: {
      ...defaultCommonFilters,
      ...defaultSkills,
      ...defaultCommitmentFilters,
      ...defaultRole,
      ...defaultWorkingTimezoneJobFormValues,
    },
    defaultParamsValues: {
      ...defaultCommonFilters,
      ...defaultSkillsParams,
      ...defaultCommitmentParamsFilters,
      ...defaultRoleParams,
      ...defaultWorkingTimezoneJobFormValues,
    },
    mapParamsToFetchValues: normalizeRolesAndSkillsFormat,
  }),
);

export const defaultPublicJobFiltersFormValues: PublicJobFiltersFormValuesType =
  {
    ...defaultRate,
    ...defaultLocation,
    ...defaultJobType,
    ...defaultSkills,
    ...defaultCommitmentFilters,
    ...defaultRole,
  };

const defaultLocationParamsFilters: Location = {
  work_from_anywhere: undefined,
  work_from_location: undefined,
  custom_location: undefined,
  country_code: undefined,
  state: undefined,
  city: undefined,
};

export const defaultPublicJobFiltersParamsValues: PublicJobFiltersFetchParams =
  {
    ...defaultRate,
    ...defaultJobType,
    ...defaultLocationParamsFilters,
    ...defaultSkillsParams,
    ...defaultCommitmentParamsFilters,
    ...defaultRoleParams,
    ...defaultPage,
  };

export const usePublicJobFilters = createFiltersSearchParamsHook<
  JobFiltersFormValuesType,
  JobFiltersFetchParams
>(
  new FiltersManager({
    defaultFormValues: defaultPublicJobFiltersFormValues,
    defaultParamsValues: defaultPublicJobFiltersParamsValues,
    mapParamsToFetchValues: normalizeRolesAndSkillsFormat,
  }),
);

export const feedbackParamsSchema = z.object({
  has_been_hired: zodStringBoolean.nullable(),
  high_rating: zodStringBoolean.nullable(),
});

export type TalentFeedbackFilters = z.infer<typeof feedbackParamsSchema>;

export const defaultFeedback: RequiredWithUndefined<TalentFeedbackFilters> = {
  has_been_hired: undefined,
  high_rating: undefined,
};

export const useTalentFilters = createFiltersSearchParamsHook<
  TalentFiltersFormValuesType,
  TalentFiltersFetchParams
>(
  new FiltersManager({
    paramsToIgnore: ["location", "talent_status"], // just for ui purpose
    defaultFormValues: {
      ...defaultSearch,
      ...defaultTalentLocation,
      ...defaultExperience,
      ...defaultWorkingTimezoneTalentFilter,
      ...defaultRole,
      ...defaultSkills,
      ...defaultFeedback,
    },
    mapParamsToValues: (values) => {
      const newValues = { ...values } as TalentFiltersFormValuesType;
      return mapWorkingTimezoneTalentFiltersParams(newValues);
    },
    mapParamsToFetchValues: normalizeRolesAndSkillsFormat,
  }),
);
